import { type ReactNode } from "react";
import { Flag } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Visual primitives shared between `PrActionCards` (PR-context surface)
 * and `TaskStarterCards` (no-PR surface). Both render a centered wrapping
 * row of compact pill-shaped prompt buttons, optionally led by a full-width
 * onboarding suggestion card; keeping the styles in one place ensures the
 * surfaces stay visually identical
 * when design tweaks land.
 */

/**
 * Wrapping, left-aligned container for a row of prompt pills. gap-x stays
 * tight so the four PR action pills keep fitting the 528px composer column
 * on one line; gap-y is roomier so wrapped rows don't sit flush.
 */
export const PROMPT_PILL_ROW_CLASS =
  "flex w-full flex-wrap items-center justify-start gap-x-1 gap-y-2";

/**
 * Starter-pill row variant: wrapping (stacking) is reserved for small
 * screens where the composer column genuinely runs out of width. From
 * `sm` up the starter pill and the MCP pill always share one line: the
 * composer column is capped at 528px there, which fits both pills'
 * natural widths, and in the rare case a wider font pushes past that the
 * pill labels truncate (see PromptPill) instead of dropping onto a second
 * line.
 */
export const STARTER_PILL_ROW_CLASS = cn(
  PROMPT_PILL_ROW_CLASS,
  "sm:flex-nowrap",
);

/**
 * The lead starter pill hugs its label from `sm` up (with roomier padding
 * than the compact PR pills) so the MCP pill can absorb the rest of the
 * row; on small screens it grows to fill its wrapped line.
 */
export const LEAD_STARTER_PILL_LAYOUT_CLASS = "grow sm:flex-none sm:px-7";

/**
 * Pill chrome shared by the prompt pills and the MCP pill-cluster
 * segments (which override the rounding to form a segmented control).
 */
export const PROMPT_PILL_SEGMENT_CLASS =
  "flex h-[30px] cursor-pointer items-center border border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-inset-hover)] focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[var(--focus-border)]";

// px-2.5 keeps the four PR action pills on one line in the 528px composer
// column (px-3 overflows it by 4px).
export const PROMPT_PILL_CLASS = cn(
  PROMPT_PILL_SEGMENT_CLASS,
  "min-w-0 gap-1.5 rounded-full px-2.5",
);

/**
 * Card chrome for full-width suggestion cards rendered above the pill row.
 */
export const SUGGESTION_CARD_CLASS =
  "flex w-full items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5";

/** Title + description stack used inside a suggestion card. */
export function SuggestionCardTitleBlock({
  title,
  description,
  className,
}: {
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 flex-1 flex-col", className)}>
      <span className="type-body-s-medium text-neutral-900 dark:text-neutral-100">
        {title}
      </span>
      <span className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">
        {description}
      </span>
    </span>
  );
}

export interface PromptPillProps {
  label: string;
  /** Icon node rendered at the start of the pill. */
  icon: ReactNode;
  /** Omitted by the page-level skeleton, whose root is `pointer-events-none`. */
  onClick?: () => void;
  className?: string;
}

/**
 * Standard prompt-backed pill used by both surfaces. Pure presentation;
 * leaves analytics and prompt dispatch to the parent's `onClick`.
 */
export function PromptPill({
  label,
  icon,
  onClick,
  className,
}: PromptPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(PROMPT_PILL_CLASS, className)}
    >
      <span className="flex size-4 shrink-0 items-center justify-center overflow-visible text-neutral-500 dark:text-neutral-400">
        {icon}
      </span>
      <span className="type-body-s-medium truncate text-neutral-700 dark:text-neutral-200">
        {label}
      </span>
    </button>
  );
}

/** Icon keys for the starter options rendered as pills. */
export type StarterOptionIcon = "cleanup-flag";

export function starterPillIcon(icon: StarterOptionIcon): ReactNode {
  switch (icon) {
    case "cleanup-flag":
      return <Flag className="size-4" strokeWidth={1.75} />;
  }
}

/**
 * Static face of the lead starter option, kept with the pill primitives so
 * the page-level skeleton can paint it without importing `TaskStarterCards`
 * and its org query stack. The prompt it dispatches lives with the other
 * starter options, in `STARTER_PROMPT_CARDS`.
 */
export const CLEANUP_FLAG_STARTER_PILL: {
  title: string;
  icon: StarterOptionIcon;
} = {
  title: "Clean up a stale feature flag",
  icon: "cleanup-flag",
};

/**
 * Shimmer placeholder sized to match a resolved `PromptPill` (30px tall),
 * so the skeleton row swaps to the final pills without a layout shift.
 * `className` sizes the wrapper (e.g. `w-32`, `grow`) so flex behavior
 * matches the pill it stands in for.
 */
export function PromptPillSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "pointer-events-none flex h-[30px] w-44 items-center",
        className,
      )}
    >
      <Skeleton className="h-[30px] w-full rounded-full" />
    </div>
  );
}

/**
 * Shimmer placeholder for the lead starter pill's slot. Unlike
 * `PromptPillSkeleton`, it renders the resolved pill's chrome with
 * invisible icon/label sizing content, so the slot keeps the exact
 * intrinsic width the settled `PromptPill` will have — `sm:flex-none`
 * on the wrapper then holds the resolved content width instead of an
 * arbitrary skeleton width. The second (grow) slot uses it too so both
 * shimmers share the same chrome.
 */
export function StarterPromptPillSkeleton({
  className,
}: {
  className: string;
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden
      data-slot="starter-prompt-pill-skeleton"
      className={cn(
        PROMPT_PILL_CLASS,
        "pointer-events-none relative justify-center",
        className,
      )}
    >
      <span className="flex size-4 shrink-0 items-center justify-center overflow-visible text-neutral-500 opacity-0 dark:text-neutral-400">
        {starterPillIcon(CLEANUP_FLAG_STARTER_PILL.icon)}
      </span>
      <span className="type-body-s-medium truncate text-neutral-700 opacity-0 dark:text-neutral-200">
        {CLEANUP_FLAG_STARTER_PILL.title}
      </span>
      <Skeleton className="absolute inset-0 rounded-full" />
    </button>
  );
}
